import isUndefined from 'lodash/isUndefined';
import sortByLodash from 'lodash/sortBy';
import React, {Component} from 'react';
import PropTypes from 'prop-types';
import loadable from '@loadable/component';
import {connect} from 'react-redux';
import {compose} from 'redux';
import {getWindow} from 'ssr-window';
import IconMap from '@spothero/icons/map';
import IconMenu from '@spothero/icons/menu';
import Button from '@spothero/ui/button';
import APIUtils from '@spothero/utils/api';
import DOMUtils from '@spothero/utils/dom';
import AlertModal from 'common/alert/AlertModal';
import QuickFilters from 'filters/QuickFilters';
import {setNoSpotsFound, toggleMobileListView} from 'search/search-actions';
import {setLocation} from 'search-request/search-request-actions';
import {getSpotsMobile} from 'spots/spots-actions';
import WithSearch from './WithSearch';
import WithParkingNearMe from './WithParkingNearMe';
import SearchLayoutMobile from './layout/SearchLayoutMobile';
import SpotListSearchControlsCondensed from './spot-list/SpotListSearchControlsCondensed';
import SpotList from './spot-list/SpotList';
import SpotListSearchControls from './spot-list/SpotListSearchControls';
import SpotListItemTransient from './spot-list/SpotListItemTransient';

const window = getWindow();
const SpotDetails = loadable(() => import('spot-details/SpotDetails'));
const SearchMap = loadable(() => import('./map/SearchMap'));
const DestinationInfoModalMobile = loadable(() =>
    import('destination/DestinationInfoModalMobile')
);

export class SearchMobileTransient extends Component {
    static propTypes = {
        searchRequestId: PropTypes.string,
        canonicalUrl: PropTypes.string,
        starts: PropTypes.string,
        ends: PropTypes.string,
        activeSearchTitle: PropTypes.string,
        city: PropTypes.object,
        spots: PropTypes.arrayOf(PropTypes.object),
        findParkingNearMe: PropTypes.func,
        selectedSpotId: PropTypes.number,
        loadMobileSpots: PropTypes.func.isRequired,
        isFetchComplete: PropTypes.bool.isRequired,
        numActiveFilters: PropTypes.number,
        idealDistance: PropTypes.number,
        mobilePaginationLimit: PropTypes.number,
        isMobileListViewActive: PropTypes.bool.isRequired,
        sortBy: PropTypes.string.isRequired,
        geolocated: PropTypes.bool,
        onSpotDetailsHidden: PropTypes.func,
        setNewSearchLocation: PropTypes.func,
        isLowInventoryEnabled: PropTypes.bool,
        isSoldOutEnabled: PropTypes.bool,
        showMobileListView: PropTypes.func.isRequired,
        onShowDetailsClick: PropTypes.func.isRequired,
        onNoSpotsFound: PropTypes.func.isRequired,
    };

    constructor(props) {
        super(props);

        this._minDistance = 0;
        this._pageCount = 1;
        this._spotLoaderEnabled = true;

        this.state = {
            showListLoader: false,
            showLoadingComplete: false,
            showGeolocateModal: false,
            showInfoModal: false,
        };
    }

    componentDidMount() {
        // check geolocate environment after first render to avoid SSR errors of div mismatches
        const showGeolocate = this._checkGeolocateEnvironment();

        if (!showGeolocate && this.props.isMobileListViewActive) {
            this._newSearch({
                initial: true,
            });
        }

        this._cacheSelectors();
    }

    componentDidUpdate(prevProps, prevState) {
        const {
            isFetchComplete,
            searchRequestId,
            starts,
            ends,
            isMobileListViewActive,
            geolocated,
        } = this.props;
        const {
            isFetchComplete: prevIsFetchComplete,
            searchRequestId: prevSearchRequestId,
            starts: prevStarts,
            ends: prevEnds,
            isMobileListViewActive: prevIsMobileListViewActive,
            geolocated: prevGeolocated,
        } = prevProps;

        if (!prevIsFetchComplete && isFetchComplete) {
            this._fetchCompleted();
        }

        if (prevSearchRequestId !== searchRequestId) {
            this._newSearch({
                initial: false,
            });
        } else {
            if (prevStarts !== starts || prevEnds !== ends) {
                this._minDistance = 0;
                this._pageCount = 1;

                this._newSearch({
                    dateTimeChanged: true,
                });
            }
        }

        if (!prevIsMobileListViewActive && isMobileListViewActive) {
            this._cacheSelectors();
        }

        if (!prevGeolocated && geolocated) {
            this._newSearch({
                initial: true,
            });
        }
    }

    componentWillUnmount() {
        if (this._spotsLoader) {
            this._spotsLoader.cancel();
        }

        this._spotLoaderEnabled = false;
    }

    _onConfirmGeolocate = () => {
        this.setState({
            showListLoader: true,
        });

        if (!isUndefined(window) && window.navigator.geolocation) {
            window.navigator.geolocation.getCurrentPosition(
                this._userPositionReturned,
                () => {
                    // eslint-disable-next-line no-alert
                    alert(
                        'Sorry, your browser does not allow geolocation, please search an address.'
                    );

                    this._onRejectGeolocate();
                }
            );
        } else {
            // eslint-disable-next-line no-alert
            alert(
                'Sorry, your browser does not support geolocation, please search an address.'
            );

            this._onRejectGeolocate();
        }
    };

    _onRejectGeolocate = () => {
        this._onLoadingComplete();
    };

    _onLoadingComplete = () => {
        this.setState({
            showListLoader: false,
            showLoadingComplete: true,
        });
    };

    _onShowGeolocateModalHidden = () => {
        this.setState({
            showGeolocateModal: false,
        });
    };

    _onListScroll = () => {
        const {sortBy} = this.props;
        const {showListLoader} = this.state;

        if (
            !showListLoader &&
            sortBy !== 'price' &&
            this._spotListEl.scrollTop ===
                this._spotListEl.scrollHeight - this._spotListEl.offsetHeight
        ) {
            this._loadNextPage();
        }
    };

    _onToggleInfoModal = evt => {
        this.setState((prevState, props) => {
            return {
                showInfoModal: !prevState.showInfoModal,
            };
        });
    };

    _onViewToggle = evt => {
        const {isMobileListViewActive, showMobileListView} = this.props;

        showMobileListView(!isMobileListViewActive);
    };

    _switchToListView = () => {
        this.props.showMobileListView(true);
    };

    _checkGeolocateEnvironment() {
        const showGeolocateModal = Boolean(
            !this.props.geolocated &&
                window.location.pathname.match(/parking-near-me/)
        );

        this.setState({
            showGeolocateModal,
        });

        return showGeolocateModal;
    }

    _cacheSelectors() {
        this._spotListEl = DOMUtils.el('.SpotListContainer');
    }

    _fetchCompleted() {
        this.setState({
            showListLoader: false,
            showLoadingComplete: false,
        });
    }

    _newSearch = ({dateTimeChanged = false, initial = false}) => {
        const {
            numActiveFilters,
            idealDistance,
            loadMobileSpots,
            onNoSpotsFound,
        } = this.props;

        if (!initial) {
            this.setState({
                showListLoader: true,
            });
        }

        loadMobileSpots({
            limit: 8,
            maxDistance: idealDistance,
            dateTimeChanged,
        }).then(response => {
            if (!this._spotLoaderEnabled) {
                return;
            }

            const {
                value: {results},
            } = response;

            if (!results || results.length < 4 || numActiveFilters > 0) {
                if (!results) {
                    this._minDistance = 100;
                }

                // Ccccccrazy repeat tries for additional spots when none found initially
                // to be updated when we have real search pagination
                this._loadNextPage({
                    onNoResults: () => {
                        this._loadNextPage({
                            onNoResults: () => {
                                this._loadNextPage({
                                    onNoResults: () => {
                                        this._onLoadingComplete();

                                        onNoSpotsFound(true);
                                    },
                                });
                            },
                        });
                    },
                });
            }
        });
    };

    _loadNextPage = ({onNoResults = null} = {}) => {
        const {
            spots,
            loadMobileSpots,
            numActiveFilters,
            idealDistance,
            mobilePaginationLimit,
        } = this.props;
        let minDistance = spots.length
            ? sortByLodash(spots, 'distance').reverse()[0].distance - 30
            : 0;

        if (this._pageCount > 9) {
            this._onLoadingComplete();

            return;
        }

        minDistance =
            this._minDistance && this._minDistance === minDistance
                ? minDistance + 500
                : minDistance < this._minDistance
                ? this._minDistance + 500
                : minDistance;

        this.setState({
            showListLoader: true,
        });

        const params = {
            minDistance,
            maxDistance: idealDistance,
        };

        if (mobilePaginationLimit) {
            params.limit = mobilePaginationLimit;
        }

        this._spotsLoader = APIUtils.makeCancelable(loadMobileSpots(params));
        this._spotsLoader.promise
            .then(({value}) => {
                const {results} = value;

                if (
                    (!results || !results.length || numActiveFilters > 0) &&
                    onNoResults
                ) {
                    onNoResults();
                }
            })
            .catch(error => {
                return error;
            });

        this._minDistance = minDistance;
        this._pageCount += 1;
    };

    _userPositionReturned = response => {
        if (!response.coords) {
            return false;
        }

        const {latitude, longitude} = response.coords;

        this.props.setNewSearchLocation({
            latitude,
            longitude,
            title: 'Current Location',
            geolocated: true,
        });

        this.setState({
            showGeolocateModal: false,
        });
    };

    render() {
        const {
            searchRequestId,
            starts,
            ends,
            activeSearchTitle,
            city,
            canonicalUrl,
            selectedSpotId,
            spots,
            findParkingNearMe,
            geolocated,
            isMobileListViewActive,
            onSpotDetailsHidden,
            isLowInventoryEnabled,
            isSoldOutEnabled,
            onShowDetailsClick,
        } = this.props;
        const {
            showListLoader,
            showLoadingComplete,
            showGeolocateModal,
            showInfoModal,
        } = this.state;
        const numSpots = spots.length;
        const transientSpots = spots.filter(({selectedRate}) => {
            // ensure spots with either missing selectedRate
            // or those without the proper selectedRate.rule_type are not offered to users
            return selectedRate?.rule_type !== 'monthly';
        });

        return (
            <SearchLayoutMobile
                SearchMap={
                    <SearchMap
                        spots={transientSpots}
                        findParkingNearMe={findParkingNearMe}
                        isSoldOutEnabled={isSoldOutEnabled}
                        onShowDetailsClick={onShowDetailsClick}
                    />
                }
                SpotDetails={
                    <SpotDetails
                        isMobile
                        spotId={selectedSpotId}
                        onCloseClick={onSpotDetailsHidden}
                        isLowInventoryEnabled={isLowInventoryEnabled}
                    />
                }
                SpotList={
                    <SpotList
                        spots={transientSpots}
                        selectedSpotId={selectedSpotId}
                        canonicalUrl={canonicalUrl}
                        validFrom={starts}
                        validThrough={ends}
                        isLowInventoryEnabled={isLowInventoryEnabled}
                        isSoldOutEnabled={isSoldOutEnabled}
                        onShowDetailsClick={onShowDetailsClick}
                        showListLoader={showListLoader}
                        showLoadingComplete={showLoadingComplete}
                        loadNextPage={this._loadNextPage}
                        SpotListItem={SpotListItemTransient}
                    />
                }
                SpotListSearchControls={
                    <SpotListSearchControls
                        numSpots={numSpots}
                        onToggleInfoModal={this._onToggleInfoModal}
                    />
                }
                SpotListSearchControlsCondensed={
                    <SpotListSearchControlsCondensed
                        starts={starts}
                        ends={ends}
                        title={activeSearchTitle}
                    />
                }
                DestinationInfoModalMobile={
                    showInfoModal && (
                        <DestinationInfoModalMobile
                            title={city.display_name}
                            city={city.display_name}
                            state={city.state}
                            description={city.description}
                            onHidden={this._onToggleInfoModal}
                        />
                    )
                }
                QuickFilters={<QuickFilters className="QuickFilters-map" />}
                AlertModal={
                    showGeolocateModal &&
                    !geolocated && (
                        <AlertModal
                            className="GeolocationModal"
                            title="Where ya headed?"
                            confirmButtonLabel="My Current Location"
                            showRejectButton
                            showClose={false}
                            rejectButtonLabel="Somewhere Else"
                            onHidden={this._onShowGeolocateModalHidden}
                            onConfirm={this._onConfirmGeolocate}
                            onReject={this._onRejectGeolocate}
                        />
                    )
                }
                ToggleButton={
                    <Button
                        className="Search-view-toggle"
                        color="primary"
                        iconPosition="left"
                        onClick={this._onViewToggle}
                    >
                        {isMobileListViewActive ? (
                            <>
                                <IconMap />
                                Map
                            </>
                        ) : (
                            <>
                                <IconMenu />
                                List
                            </>
                        )}
                    </Button>
                }
                spots={spots}
                selectedSpotId={selectedSpotId}
                isMobileListViewActive={isMobileListViewActive}
                searchRequestId={searchRequestId}
                switchToListView={this._switchToListView}
            />
        );
    }
}

const mapStateToProps = state => {
    const {
        city: {data: city},
        filters: {numActive},
        spots: {isPending: areSpotsLoading},
        search: {
            data: {
                breadcrumbs: {
                    activeBreadcrumb: {label: activeSearchTitle},
                },
                geolocated,
                mobilePaginationLimit,
                isMobileListViewActive,
            },
        },
        searchRequest: {id, starts, ends},
    } = state;

    return {
        searchRequestId: id,
        activeSearchTitle,
        city,
        starts,
        ends,
        isFetchComplete: !areSpotsLoading,
        geolocated,
        numActiveFilters: numActive,
        mobilePaginationLimit,
        isMobileListViewActive,
    };
};

const mapDispatchToProps = {
    loadMobileSpots: getSpotsMobile,
    setNewSearchLocation: setLocation,
    showMobileListView: toggleMobileListView,
    onNoSpotsFound: setNoSpotsFound,
};

const enhance = compose(
    WithSearch,
    WithParkingNearMe,
    connect(mapStateToProps, mapDispatchToProps)
);

export default enhance(SearchMobileTransient);
